/**
 * Utility functions for extracting data from Auth0 user objects
 */

import type { UserContext } from '../types';

export interface PlatformUser {
  uuid: string;
  id: number;
  delphius_role: string;
}

export interface PrimaryEntity {
  entity_id: number;
  subdomain: string;
  uuid: string;
}

/**
 * Complete Auth0 user object structure with OpenGov custom claims
 */
export interface Auth0User {
  // OpenGov custom claims
  c_type?: string;
  'https://opengov.com/urn'?: string;
  'https://opengov.com/platform_user'?: PlatformUser;
  'https://opengov.com/primary_entity'?: PrimaryEntity;

  // Standard OIDC claims (PII - should NOT be logged)
  given_name?: string;
  family_name?: string;
  nickname?: string;
  name?: string;
  picture?: string;
  updated_at?: string;
  email?: string;

  // Auth0 subject identifier (safe to log)
  sub?: string;

  // Allow additional properties for entity entitlements and other dynamic claims
  [key: string]: unknown;
}

/**
 * Extract platform user information from Auth0 user object
 * @param user - Auth0 user object
 * @returns Platform user object or undefined
 */
export function extractPlatformUser(user: Auth0User | undefined): PlatformUser | undefined {
  if (!user) return undefined;

  const platformUser = user['https://opengov.com/platform_user'];
  if (!platformUser) return undefined;

  if (typeof platformUser === 'object') {
    return platformUser as PlatformUser;
  }

  return undefined;
}

/**
 * Extract primary entity information from Auth0 user object
 * @param user - Auth0 user object
 * @returns Primary entity object or undefined
 */
export function extractPrimaryEntity(user: Auth0User | undefined): PrimaryEntity | undefined {
  if (!user) return undefined;

  const primaryEntity = user['https://opengov.com/primary_entity'];
  if (!primaryEntity) return undefined;

  if (typeof primaryEntity === 'object') {
    return primaryEntity as PrimaryEntity;
  }

  return undefined;
}

/**
 * Extract safe (non-PII) user context from Auth0 user object for logging.
 *
 * SAFE fields (extracted):
 * - sub (Auth0 user ID)
 * - name (user display name for log readability)
 * - platform_user.uuid, platform_user.id, platform_user.delphius_role
 *
 * UNSAFE fields (NEVER extracted):
 * - email, phone_number, address, etc.
 *
 * @param user - Auth0 user object (from useAuth0 hook)
 * @returns Non-PII user context or null if user is unavailable
 */
export function extractSafeUserContext(user: Auth0User | undefined): UserContext | null {
  if (!user) return null;

  const platformUser = extractPlatformUser(user);

  return {
    user_id: user.sub || 'unknown',
    user_name: user.name,
    user_platform_user_id: platformUser?.uuid,
    user_role: platformUser?.delphius_role,
  };
}

/**
 * Extract full user name from Auth0 user object
 * @param user - Auth0 user object
 * @returns Full user name string or undefined
 */
export function extractFullUserName(user: Auth0User | undefined): string | undefined {
  if (!user) return undefined;

  // A blank name is not a label. The avatar button would lose its accessible name.
  const name = user.name?.trim();
  if (name) return name;

  const givenName = user.given_name?.trim();
  const familyName = user.family_name?.trim();
  if (givenName && familyName) return `${givenName} ${familyName}`;
  return givenName || familyName || undefined;
}

/** First character of a name part, or undefined when the part is missing or blank. */
function firstInitial(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  // Spread so an astral character stays one glyph. Index 0 is only a UTF-16 unit.
  const [firstCharacter] = [...trimmed];
  return firstCharacter?.toUpperCase();
}

/**
 * First word and last word. A middle name is not an initial.
 * One word returns that one letter. A blank name returns undefined.
 */
function initialsFromName(name: string | undefined): string | undefined {
  const words =
    name
      ?.trim()
      .split(/\s+/)
      .filter((word) => word.length > 0) ?? [];
  const first = firstInitial(words[0]);
  const last = words.length > 1 ? firstInitial(words[words.length - 1]) : undefined;
  if (first && last) return `${first}${last}`;
  return first;
}

/**
 * Extract user initials from Auth0 user object for avatar display
 * @param user - Auth0 user object
 * @returns User initials string (default: '-')
 */
export function extractUserInitials(user: Auth0User | undefined): string {
  if (!user) return '-';

  // Both split claims win, in given-then-family order ("Morgan Roberts" -> "MR").
  // A single split claim must not hide `name`, or the menu label and the avatar disagree.
  const givenInitial = firstInitial(user.given_name);
  const familyInitial = firstInitial(user.family_name);
  if (givenInitial && familyInitial) {
    return `${givenInitial}${familyInitial}`;
  }

  const fromName = initialsFromName(user.name);
  if (fromName) return fromName;

  return givenInitial ?? familyInitial ?? '-';
}
